import { Navigate } from 'react-router-dom'
import { useProfileStore } from '@/stores/profileStore'
import { ProtectedRoute } from './ProtectedRoute'

type AppRole = 'user' | 'supporter' | 'admin'

interface RoleProtectedRouteProps {
  children: React.ReactNode
  allowedRoles: AppRole[]
  fallbackPath?: string
}

export function RoleProtectedRoute({
  children,
  allowedRoles,
  fallbackPath = '/u/dashboard',
}: RoleProtectedRouteProps) {
  const { profile } = useProfileStore()

  // Spin only while we genuinely have no profile yet. A re-fetch *after* the
  // first load (e.g. dashboard refresh after a rating writes assessment_state)
  // briefly sets isLoading=true with profile still populated — swapping in a
  // spinner there would unmount the child page and re-mount it on completion,
  // which loops if the child also fires fetchProfile on mount.
  if (!profile) {
    return (
      <ProtectedRoute>
        <div className="min-h-screen flex items-center justify-center bg-[#FBF9F5]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF5A]" />
        </div>
      </ProtectedRoute>
    )
  }

  // Check if user's role is in the allowed roles
  if (!allowedRoles.includes(profile.role)) {
    // Redirect to appropriate dashboard based on role
    let redirectPath = fallbackPath

    switch (profile.role) {
      case 'admin':
        redirectPath = '/admin'
        break
      case 'supporter':
        redirectPath = '/s/dashboard'
        break
      case 'user':
      default:
        redirectPath = '/u/dashboard'
        break
    }

    return <Navigate to={redirectPath} replace />
  }

  return <ProtectedRoute>{children}</ProtectedRoute>
}

// Convenience components for common role restrictions
export function UserRoute({ children }: { children: React.ReactNode }) {
  return (
    <RoleProtectedRoute allowedRoles={['user']}>
      {children}
    </RoleProtectedRoute>
  )
}

export function SupporterRoute({ children }: { children: React.ReactNode }) {
  return (
    <RoleProtectedRoute allowedRoles={['supporter']} fallbackPath="/u/dashboard">
      {children}
    </RoleProtectedRoute>
  )
}

export function AdminRoute({ children }: { children: React.ReactNode }) {
  return (
    <RoleProtectedRoute allowedRoles={['admin']} fallbackPath="/u/dashboard">
      {children}
    </RoleProtectedRoute>
  )
}

// Allow multiple roles (e.g., supporter OR admin)
export function SupporterOrAdminRoute({ children }: { children: React.ReactNode }) {
  return (
    <RoleProtectedRoute allowedRoles={['supporter', 'admin']} fallbackPath="/u/dashboard">
      {children}
    </RoleProtectedRoute>
  )
}
